import { useState } from 'react';
import { useEffect } from 'react';
import {
  isDesktopApp, minimizeApp, onUpdateState, quitApp,
  updateDownload, updateInstall, type UpdateState,
} from '../print';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'] as const;

export type ClockOutResult =
  | { ok: true; fullName: string; workedMin: number }
  | { ok: false; reason: 'pin' | 'noopen' };

/**
 * Ворота кассы. Вход по PIN открывает явку на сервере;
 * «Я ухожу» — тот же PIN, но явка закрывается и касса
 * прощается с человеком, назвав отработанное время.
 */
export function Pin({ place, onSubmit, onClockOut }: {
  place: string;
  onSubmit: (pin: string) => Promise<boolean | string>;
  onClockOut: (pin: string) => Promise<ClockOutResult>;
}) {
  const [mode, setMode] = useState<'in' | 'out'>('in');
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [upd, setUpd] = useState<UpdateState | null>(null);
  useEffect(() => onUpdateState(setUpd), []);
  const [busy, setBusy] = useState(false);
  const [bye, setBye] = useState<{ fullName: string; workedMin: number } | null>(null);

  const reset = (m: 'in' | 'out') => { setMode(m); setPin(''); setErr(''); };

  const press = async (k: string) => {
    setErr('');
    if (k === 'C') return setPin('');
    if (k === '⌫') return setPin((p) => p.slice(0, -1));
    const next = (pin + k).slice(0, 4);
    setPin(next);
    if (next.length < 4) return;

    setBusy(true);
    if (mode === 'in') {
      const ok = await onSubmit(next);
      setBusy(false);
      // строка вместо false — собственное сообщение сервера (пауза)
      if (typeof ok === 'string') { setPin(''); setErr(ok); }
      else if (!ok) { setPin(''); setErr('PIN не подошёл'); }
    } else {
      const res = await onClockOut(next);
      setBusy(false);
      setPin('');
      if (res.ok) setBye({ fullName: res.fullName, workedMin: res.workedMin });
      else setErr(res.reason === 'noopen'
        ? 'Открытой явки нет — уход уже отмечен'
        : 'PIN не подошёл');
    }
  };

  if (bye) {
    const h = Math.floor(bye.workedMin / 60);
    const m = bye.workedMin % 60;
    return (
      <div className="gate">
        <span className="eyebrow">{bye.fullName}</span>
        <h1>Смена: {h} ч {m} мин</h1>
        <p className="hint">Явка закрыта, часы уже в отчёте владельца. Хорошего вечера!</p>
        <button className="btn accent" style={{ width: 'min(280px, 90vw)' }}
          onClick={() => { setBye(null); reset('in'); }}>Готово</button>
      </div>
    );
  }

  return (
    <div className="gate">
      <span className="eyebrow">{place}</span>
      <h1>{mode === 'in' ? 'Введите PIN' : 'PIN для отметки ухода'}</h1>
      <div className="pin-dots" aria-label={`Введено цифр: ${pin.length} из 4`}>
        {[0, 1, 2, 3].map((i) => <i key={i} className={i < pin.length ? 'on' : ''} />)}
      </div>
      <p className="error">{err}</p>
      <div className="numpad">
        {KEYS.map((k) => (
          <button key={k} className="btn" disabled={busy} onClick={() => press(k)}>{k}</button>
        ))}
      </div>
      {mode === 'in'
        ? <button className="btn ghost" onClick={() => reset('out')}>Я ухожу — отметить уход</button>
        : <button className="btn ghost" onClick={() => reset('in')}>← Ко входу</button>}

      {/* Автообновление: плашка живёт на экране PIN — смена заперта,
          деньги закрыты, обновляться безопасно. Очередь чеков лежит
          на диске и переживает перезапуск: неотправленное уедет само */}
      {isDesktopApp() && upd && upd.state !== 'error' && (
        <div className="update-card">
          {upd.state === 'available' && (
            <>
              <b>Доступно обновление {upd.version}</b>
              <button className="btn primary" onClick={updateDownload}>
                Обновить
              </button>
            </>
          )}
          {upd.state === 'downloading' && (
            <b>Скачивается… {upd.percent}%</b>
          )}
          {upd.state === 'ready' && (
            <>
              <b>Обновление {upd.version} готово</b>
              <button className="btn primary" onClick={updateInstall}>
                Перезапустить и установить
              </button>
              <small>Неотправленные чеки сохранятся и уедут после перезапуска</small>
            </>
          )}
        </div>
      )}

      {/* Только в программе для Windows. Касса живёт во весь экран
          без рамки, а на планшете нет ни Alt+F4, ни крестика — без
          этих кнопок человек заперт и тянется к кнопке питания.
          Экран PIN — единственное безопасное место: смена заперта,
          деньги не открыты. Закрытие идёт через оболочку, и она
          предупредит, если чеки ещё не ушли на сервер */}
      {isDesktopApp() && mode === 'in' && (
        <div className="row" style={{ marginTop: 16 }}>
          <button className="btn ghost small" onClick={minimizeApp}>
            Свернуть — на рабочий стол
          </button>
          <button className="btn ghost small" onClick={quitApp}>
            Закрыть кассу
          </button>
        </div>
      )}
    </div>
  );
}
